import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { setAuth } from '../Redux/authSlice';
import { RootState } from '../Redux/store';
import { translations } from '../utils/translations';
import "./sidebar.css"

interface SidebarProps {
    isVisible: boolean;
    toggleSidebar: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isVisible, toggleSidebar }) => {
    const [userRole, setUserRole] = useState<string | null>(null);
    const [branchName, setBranchName] = useState<string | null>(null);
    const [showFeesOptions, setShowFeesOptions] = useState(false);
    const [showClassOptions, setShowClassOptions] = useState(false);
    const [showStudentsOptions, setShowStudentsOptions] = useState(false);
    const [showExamsOptions, setShowExamsOptions] = useState(false);
    const [showFinanceOptions, setShowFinanceOptions] = useState(false);
    const [showReportsOptions, setShowReportsOptions] = useState(false);
    const dispatch = useDispatch();
    const auth = useSelector((state: RootState) => state.auth.value);
    const language = useSelector((state: RootState) => state.language.language);
    const t = translations[language as keyof typeof translations];
    // Removed local isSidebarVisible state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [userInfo, setUserInfo] = useState<{ firstName: string; lastName: string; branchLogo: string; branchName: string; }>({ firstName: '', lastName: '', branchLogo: '', branchName: '' });
    const sidebarRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchUserData = async () => {
            setLoading(true);
            try {
                const token = getCookie('token');
                if (!token) {
                    dispatch(setAuth(false));
                    return;
                }
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                const response = await axios.get('https://api.sominnovations.xyz/users/user');
                const { first_name, last_name, branch_logo, branch_name } = response.data.user;
                setUserInfo({ firstName: first_name, lastName: last_name, branchLogo: branch_logo, branchName: branch_name });
                setUserRole(response.data.user.role);
                setBranchName(response.data.user.branch_name);
                dispatch(setAuth(true));
            } catch (error) {
                console.error('Error fetching user data:', error);
                dispatch(setAuth(false));
                setError('Failed to fetch user data');
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();

        const handleClickOutside = (event: MouseEvent) => {
            // Only hide sidebar on smaller screens when clicking outside
            if (window.innerWidth <= 980 && isVisible && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
                // Call props function to close, checking if passed
                toggleSidebar();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dispatch, isVisible, toggleSidebar]);

    // Removed toggleSidebarVisibility method as it's passed via props

    const getCookie = (name: string) => {
        const cookieName = name + "=";
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookieArray = decodedCookie.split(';');
        for (let i = 0; i < cookieArray.length; i++) {
            let cookie = cookieArray[i];
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1);
            }
            if (cookie.indexOf(cookieName) === 0) {
                return cookie.substring(cookieName.length, cookie.length);
            }
        }
        return '';
    };

    const toggleFeesOptions = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        setShowFeesOptions(!showFeesOptions);
    };

    const toggleReportOptions = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        setShowReportsOptions(!showReportsOptions);
    };

    const toggleExamsOptions = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        setShowExamsOptions(!showExamsOptions);
    };

    const toggleClassOptions = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        setShowClassOptions(!showClassOptions);
    };

    const toggleStudentsOptions = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        setShowStudentsOptions(!showStudentsOptions);
    };

    const toggleFinanceOptions = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        setShowFinanceOptions(!showFinanceOptions);
    };

    const sidebarStyle = {
        backgroundColor: '#5271ff',
    };

    return (
        <>
            {auth ? (
                <>
                    {/* Floating button removed */}
                    <div ref={sidebarRef} className={`sidebar-container ${isVisible ? 'visible' : ''}`}>
                        <div className="sidebar">

                            <div className="sidebar-header">
                                <img
                                    src="/images/logo.png"
                                    className="img-fluid rounded-circle"
                                    style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                    alt="Tayo Pos Logo"
                                />
                                <h6 className="mt-2 text-white">Tayo Pos</h6>
                            </div>

                            <ul className="nav nav-pills flex-column mb-auto">
                                <li>
                                    <Link to="/dashboard" className="nav-link">
                                        <i className="fa-solid fa-desktop"></i>
                                        {t.dashboard}
                                    </Link>
                                </li>
                                <li>
                                    <a href="/" className="nav-link" onClick={toggleStudentsOptions}>
                                        <i className="fa-solid fa-layer-group"></i>
                                        {t.category}
                                        <i className={`fa-solid fa-circle-chevron-${showStudentsOptions ? 'up' : 'down'}`}></i>
                                    </a>
                                </li>
                                {showStudentsOptions && (
                                    <ul className="nav flex-column">
                                        <li>
                                            <Link to="/categories" className="nav-link subNavItem">
                                                <i className="fa-solid fa-layer-group"></i>
                                                {t.category}
                                            </Link>
                                        </li>
                                    </ul>
                                )}

                                <li>
                                    <a href="/" className="nav-link" onClick={toggleClassOptions}>
                                        <i className="fa-solid fa-box-open"></i>
                                        {t.products}
                                        <i className={`fa-solid fa-circle-chevron-${showClassOptions ? 'up' : 'down'}`}></i>
                                    </a>
                                    {showClassOptions && (
                                        <ul className="nav flex-column">
                                            <li>
                                                <Link to="/Products" className="nav-link subNavItem">
                                                    <i className="fa-solid fa-box-open"></i>
                                                    {t.products}
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="/add-products" className="nav-link subNavItem">
                                                    <i className="fa-solid fa-plus"></i>
                                                    {t.addProduct}
                                                </Link>
                                            </li>
                                        </ul>
                                    )}
                                </li>
                                <li>
                                    <Link to="/purchase" className="nav-link">
                                        <i className="fa-solid fa-cart-arrow-down"></i>
                                        {t.purchases}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/add-purchase" className="nav-link">
                                        <i className="fa-solid fa-circle-plus"></i>
                                        {t.addPurchase}
                                    </Link>
                                </li>
                                <li>
                                    <a href="/" className="nav-link" onClick={toggleFeesOptions}>
                                        <i className="fa-solid fa-receipt"></i>
                                        {t.sales}
                                        <i className={`fa-solid fa-circle-chevron-${showFeesOptions ? 'up' : 'down'}`}></i>
                                    </a>
                                    {showFeesOptions && (
                                        <ul className="nav flex-column">
                                            <li>
                                                <Link to="/sales" className="nav-link subNavItem">
                                                    <i className="fa-solid fa-dollar-sign"></i>
                                                    {t.sales}
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="/pos" className="nav-link subNavItem">
                                                    <i className="fa-solid fa-circle-plus"></i>
                                                    {t.addSale}
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="/sales-return" className="nav-link subNavItem">
                                                    <i className="fa-solid fa-box"></i>
                                                    {t.salesReturn}
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="/add-sales-return" className="nav-link subNavItem">
                                                    <i className="fa-solid fa-plus"></i>
                                                    {t.salesReturn}
                                                </Link>
                                            </li>
                                        </ul>
                                    )}
                                </li>
                                <li>
                                    <a href="/" className="nav-link" onClick={toggleFinanceOptions}>
                                        <i className="fa-solid fa-wallet"></i>
                                        {t.expenses}
                                        <i className={`fa-solid fa-circle-chevron-${showFinanceOptions ? 'up' : 'down'}`}></i>
                                    </a>
                                    {showFinanceOptions && (
                                        <ul className="nav flex-column">
                                            <li>
                                                <Link to="/expenses" className="nav-link subNavItem">
                                                    <i className="fa-solid fa-money-bill"></i>
                                                    {t.expenses}
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="/expenses" className="nav-link subNavItem">
                                                    <i className="fa-solid fa-add"></i>
                                                    {t.expenses}
                                                </Link>
                                            </li>
                                        </ul>
                                    )}
                                </li>
                                {userRole === 'admin' && (
                                    <li>
                                        <Link to="/income" className="nav-link">
                                            <i className="fa-solid fa-bar-chart"></i>
                                            {t.income}
                                        </Link>
                                    </li>
                                )}
                                <li>
                                    <Link to="/customers" className="nav-link">
                                        <i className="fa-solid fa-user"></i>
                                        {t.customers}
                                    </Link>
                                </li>
                                <li>
                                    <a href="/" className="nav-link" onClick={toggleExamsOptions}>
                                        <i className="fa-solid fa-truck"></i>
                                        {t.suppliers}
                                        <i className={`fa-solid fa-circle-chevron-${showExamsOptions ? 'up' : 'down'}`}></i>
                                    </a>
                                    {showExamsOptions && (
                                        <ul className="nav flex-column">
                                            <li>
                                                <Link to="/suppliers" className="nav-link subNavItem">
                                                    <i className="fa-solid fa-list"></i>
                                                    {t.suppliers}
                                                </Link>
                                            </li>
                                        </ul>
                                    )}
                                </li>
                                <li>
                                    <a href="/" className="nav-link" onClick={toggleReportOptions}>
                                        <i className="fa-solid fa-file"></i>
                                        Reports
                                        <i className={`fa-solid fa-circle-chevron-${showReportsOptions ? 'up' : 'down'}`}></i>
                                    </a>
                                    {showReportsOptions && (
                                        <ul className="nav flex-column">
                                            <li>
                                                <Link to="/sale-report" className="nav-link subNavItem">
                                                    <i className="fa-solid fa-receipt"></i>
                                                    {t.saleReport}
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="/expense-report" className="nav-link subNavItem">
                                                    <i className="fa-solid fa-wallet"></i>
                                                    {t.expenseReport}
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="/purchase-report" className="nav-link subNavItem">
                                                    <i className="fa-solid fa-money-bill"></i>
                                                    {t.purchaseReport}
                                                </Link>
                                            </li>
                                        </ul>
                                    )}
                                </li>
                                <li>
                                    <Link to="/debts" className="nav-link">
                                        <i className="fa-solid fa-credit-card"></i>
                                        {t.debts}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/register" className="nav-link">
                                        <i className="fa-solid fa-user-plus"></i>
                                        Register User
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/users" className="nav-link">
                                        <i className="fa-solid fa-users"></i>
                                        All Users
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/Account" className="nav-link">
                                        <i className="fa-solid fa-user-circle"></i>
                                        Account
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </>
            ) : (
                <div className="error"></div>
            )}
        </>
    );
};

export default Sidebar;
